import { ChangeEvent, useMemo, useRef, useState } from "react";
import { CloseButton, Spinner } from "@chakra-ui/react";
import { MentionsInput, Mention } from "react-mentions";
import toast from "react-hot-toast";

import {
  useCreatePostMutation,
  useGetProfileQuery,
  useTagUserMutation,
} from "../../../redux/reducers/apiSlice";

import { AddImage } from "../../Assets/SvgIcons";
import {
  AddNewPostCardWrapper,
  PostButton,
  InputFirstRow,
  InputSecondRow,
  AddImageButton,
  ButtonLabel,
} from "./styles";

import { getUsersFromTags } from "../../utils/helper";
import { ProfileAvatar } from "../ProfileAvatar/ProfileAvatar";
import { useOrganization } from "../../hooks/useOrganization";
import { useMentions } from "../../hooks/useMentions";
import CustomMention from "../CustomMentions/CustomMentions";
import { SuggestionDataItem } from "../../../types";
import * as Sentry from "@sentry/react";
import { LinkPreview } from "../LinkPreview/LinkPreview";

const MAX_FILE_SIZE = 10_000_000;
const MAX_CHAR_COUNT = 2500;
const MAX_IMAGES = 6;

// ▶️ FINAL WORKING TEXTAREA STYLE
export const mentionStyle = {
  control: {
    width: "100%",
    minHeight: "80px",
    fontSize: 14,
    padding: "12px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #ddd",
    display: "block",
  },
  highlighter: {
    padding: "12px",
    minHeight: "80px",
    width: "100%",
  },
  input: {
    padding: "12px",
    minHeight: "80px",
    width: "100%",
    border: "none",
    outline: "none",
    resize: "none",
  },
  suggestions: {
    backgroundColor: "#fff",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
};

export const AddNewPostCard = () => {
  const [postType, setPostType] = useState<"TEXT" | "MEDIA">("TEXT");
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [imgFiles, setImgFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { mentionsData } = useMentions();
  const [tagUser, { isLoading: loadingTagUser }] = useTagUserMutation();

  const { currentOrganization, isCurrentUserAdmin } = useOrganization();

  const {
    data: currentUserProfileData,
    isLoading: isCurrentUserProfileDataLoading,
  } = useGetProfileQuery();

  const [createPost, { isLoading: createPostLoading }] =
    useCreatePostMutation();

  const [caption, setCaption] = useState("");
  const isSubmitting = createPostLoading || loadingTagUser;

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e?.target?.files;

    if (!fileList || fileList.length === 0) return;

    const incoming = Array.from(fileList);
    const totalCount = imgFiles.length + incoming.length;

    if (totalCount > MAX_IMAGES) {
      toast.error(`You can upload up to ${MAX_IMAGES} images per post.`);
    }

    const allowed = incoming.slice(0, MAX_IMAGES - imgFiles.length);
    if (allowed.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const nextFiles: File[] = [...imgFiles];

    allowed.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Image "${file.name}" is too large. Maximum size is 10 MB.`);
        return;
      }
      nextFiles.push(file);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        if (reader.result) {
          setPreviewImages((prev) => [...prev, reader.result!.toString()]);
        }
      };
    });

    if (nextFiles.length > 0) {
      setImgFiles(nextFiles);
      setPostType("MEDIA");
    }
  };

  const onPostSubmit = async () => {
    const hasContent = caption.trim().length > 0 || imgFiles.length > 0;
    if (!hasContent || isSubmitting) return;

    try {
      const formData = new FormData();
      formData.append("type", postType);

      if (imgFiles.length > 0) {
        imgFiles.forEach((file) => {
          formData.append("photo", file);
        });
      }
      if (caption !== "") formData.append("caption", caption);

      if (currentOrganization && isCurrentUserAdmin) {
        formData.append("ownerId", currentOrganization.id);
        formData.append("ownerType", "ORGANIZATION");
      }

      const post = await createPost(formData).unwrap();

      // Tagging should not block a successful post from being acknowledged.
      try {
        await tagUser({
          targetUserIds: getUsersFromTags(caption).map((user) => user.id),
          postId: post.id,
        });
      } catch (tagErr) {
        Sentry.captureException(tagErr);
      }

      // RESET
      setPostType("TEXT");
      setPreviewImages([]);
      setImgFiles([]);
      setCaption("");
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Posted successfully");

    } catch (e) {
      Sentry.captureException(e);
      console.error("PostSubmitError", e);
      toast.error("Something went wrong while creating your post. Try again later");
    }
  };

  const handleCloseFilePreview = () => {
    setPostType("TEXT");
    setImgFiles([]);
    setPreviewImages([]);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const firstUrlInCaption = useMemo(() => {
    const URL_REGEX = /(https?:\/\/[^\s]+)/;
    const match = caption.match(URL_REGEX);
    return match ? match[0] : null;
  }, [caption]);

  return (
    <AddNewPostCardWrapper>
      <InputFirstRow>
        {isCurrentUserProfileDataLoading && <Spinner color="red" />}

        {!isCurrentUserProfileDataLoading && (
          <div className="author-avatar">
            <ProfileAvatar
              src={
                currentOrganization && isCurrentUserAdmin
                  ? currentOrganization?.image?.url
                  : currentUserProfileData?.image?.url
              }
              name={
                currentOrganization && isCurrentUserAdmin
                  ? currentOrganization.name
                  : `${currentUserProfileData?.name.firstName} ${currentUserProfileData?.name.lastName}`
              }
              border={`2px solid #eee`}
            />
          </div>
        )}

        {/* Textarea wrapper */}
        <div className="composer-column">
          <MentionsInput
            value={caption}
            onChange={(event) => {
              const value = event.target.value;
              if (value.length <= MAX_CHAR_COUNT) {
                setCaption(value);
              }
            }}
            placeholder="What's happening?"
            style={mentionStyle}
          >
            <Mention
              trigger="@"
              data={mentionsData}
              className="mention"
              renderSuggestion={(suggestionDataItem: SuggestionDataItem) => (
                <CustomMention suggestionDataItem={suggestionDataItem} />
              )}
            />
          </MentionsInput>

          {/* Character Counter */}
          <div
            className="char-counter"
            style={{ color: caption.length >= MAX_CHAR_COUNT ? "red" : "#666" }}
          >
            {caption.length}/{MAX_CHAR_COUNT}
          </div>
        </div>
      </InputFirstRow>

      {previewImages.length > 0 && (
        <div className="img-container">
          <div className="close-img-preview" onClick={handleCloseFilePreview}>
            <CloseButton />
          </div>
          {previewImages.map((src, index) => (
            <div key={index} className="img-preview-tile">
              <img src={src} />
            </div>
          ))}
        </div>
      )}

      <InputSecondRow>
        <div>
          <AddImageButton onClick={handleBrowse} disabled={isSubmitting}>
            <AddImage />
            <ButtonLabel data-cy="cypress-add-image-label">
              Add image
            </ButtonLabel>
          </AddImageButton>

          <input
            ref={fileInputRef}
            accept=".jpg, .jpeg, .png"
            onChange={handleFile}
            name="add-image"
            type="file"
            multiple
          />
        </div>

        <PostButton
          data-cy="cypress-post-button"
          disabled={isSubmitting || (caption.trim().length === 0 && imgFiles.length === 0)}
          onClick={onPostSubmit}
        >
          {isSubmitting && <Spinner size={"xs"} />}
          Post
        </PostButton>
      </InputSecondRow>

      {firstUrlInCaption && (
        <div className="link-preview-wrapper">
          <LinkPreview url={firstUrlInCaption} />
        </div>
      )}
    </AddNewPostCardWrapper>
  );
};
